import React, { useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { FileText, Newspaper, Rocket, BarChart3, Trash2, Plus, Sparkles, Wand2, Pencil, X, Bot } from "lucide-react";
import { useAuth } from "@/lib/auth";
import AdminEditors from "./AdminEditors";
import CoverImagePicker from "@/components/CoverImagePicker";
import RichTextEditor from "@/components/RichTextEditor";
import AskAIPanel from "@/components/AskPerplexity";

const CATS = ["Macro & Policy", "Technical Analysis", "IPO Reviews", "Company Spotlights", "Option Strategies", "Real Estate", "Crypto", "Commodities", "Economy"];

function StatCard({ icon: Icon, label, value, tone }) {
  return (
    <div className="rounded-xl bg-white border border-slate-200 p-5 card-glow">
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-mono-fin uppercase tracking-widest text-slate-500">{label}</span>
        <Icon className={`w-4 h-4 ${tone || "text-[#D91E2A]"}`} />
      </div>
      <div className="font-mono-fin text-3xl text-slate-900 font-bold">{value}</div>
    </div>
  );
}

const EMPTY_BLOG = { title: "", excerpt: "", content: "", category: CATS[0], author: "Nivesh Street Desk", cover_image: "", tags: "", published: true };

function BlogEditor({ onSaved, editing, onCancelEdit }) {
  const [form, setForm] = useState(EMPTY_BLOG);
  const [busy, setBusy] = useState(false);
  const [aiBusy, setAiBusy] = useState("");
  const isEdit = !!editing;

  // Load blog into the form when the "editing" target changes.
  useEffect(() => {
    if (editing) {
      setForm({
        title: editing.title || "",
        excerpt: editing.excerpt || "",
        content: editing.content || "",
        category: editing.category || CATS[0],
        author: editing.author || "Nivesh Street Desk",
        cover_image: editing.cover_image || "",
        tags: Array.isArray(editing.tags) ? editing.tags.join(", ") : (editing.tags || ""),
        published: editing.published !== false,
      });
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      setForm(EMPTY_BLOG);
    }
  }, [editing]);

  const submit = async () => {
    if (!form.title || !form.content || form.content === "<p></p>") return toast.error("Title and content required");
    setBusy(true);
    try {
      const payload = { ...form, tags: form.tags.split(",").map(t => t.trim()).filter(Boolean) };
      if (isEdit) {
        await api.put(`/blogs/${editing.id}`, payload);
        toast.success("Blog updated");
      } else {
        await api.post("/blogs", payload);
        toast.success("Blog published!");
      }
      setForm(EMPTY_BLOG);
      onSaved?.();
      if (isEdit) onCancelEdit?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  const aiAssist = async (mode) => {
    const plain = form.content.replace(/<[^>]+>/g, " ").trim();
    if (!plain) return toast.error("Add some content first");
    setAiBusy(mode);
    try {
      const r = await api.post("/ai/article-assist", { raw_text: plain, mode });
      if (mode === "polish") {
        // Wrap in <p> so TipTap keeps it readable.
        const polished = r.data.result;
        const html = /^\s*</.test(polished) ? polished : `<p>${polished.replace(/\n\n+/g, "</p><p>")}</p>`;
        setForm({ ...form, content: html });
        toast.success("Article polished by Hermes AI");
      } else if (mode === "headline") {
        const first = (r.data.result.match(/1[\.\)]\s*(.+)/)?.[1] || "").replace(/["']/g, "").trim();
        if (first) setForm({ ...form, title: first });
        toast.success("Headline suggestions ready — top pick applied");
      } else {
        toast.info(r.data.result, { duration: 12000 });
      }
    } catch { toast.error("AI is busy, try again"); }
    finally { setAiBusy(""); }
  };

  const perplexityBrief = async () => {
    const topic = (form.title || "").trim() || (form.excerpt || "").trim();
    if (topic.length < 3) return toast.error("Add a title first so we know the topic");
    setAiBusy("brief");
    try {
      const r = await api.post("/admin/ai/brief", { topic, model: "openai/gpt-4o-mini" });
      const md = r.data.answer || "";
      // Convert simple Markdown to HTML (bullets + line breaks) so the rich editor renders it.
      const lines = md.split(/\r?\n/);
      const htmlParts = [];
      let inList = false;
      for (const raw of lines) {
        const line = raw.trim();
        if (!line) { if (inList) { htmlParts.push("</ul>"); inList = false; } continue; }
        if (/^[-*•]\s+/.test(line)) {
          if (!inList) { htmlParts.push('<ul>'); inList = true; }
          htmlParts.push(`<li>${line.replace(/^[-*•]\s+/, "").replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")}</li>`);
        } else {
          if (inList) { htmlParts.push("</ul>"); inList = false; }
          htmlParts.push(`<p>${line.replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")}</p>`);
        }
      }
      if (inList) htmlParts.push("</ul>");
      const citations = (r.data.citations || []).map((c, i) => `<li><a href="${c}" target="_blank" rel="noopener noreferrer nofollow">[${i + 1}] ${c}</a></li>`).join("");
      const sourcesBlock = citations ? `<p><em>Sources:</em></p><ul>${citations}</ul>` : "";
      const brief = `<h3>Latest developments</h3>${htmlParts.join("")}${sourcesBlock}<hr />`;
      setForm({ ...form, content: brief + (form.content || "") });
      toast.success("Perplexity brief prepended with sources");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Perplexity is unavailable");
    } finally { setAiBusy(""); }
  };

  return (
    <div className="rounded-xl bg-white border border-slate-200 p-4 sm:p-6 space-y-4" data-testid="blog-editor">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <h3 className="font-heading font-bold text-lg">{isEdit ? "Edit Blog Post" : "New Blog Post"}</h3>
          {isEdit && (
            <button onClick={onCancelEdit} className="text-xs px-2 py-1 rounded bg-slate-100 hover:bg-slate-200 text-slate-600 inline-flex items-center gap-1 font-mono-fin" data-testid="blog-cancel-edit">
              <X className="w-3 h-3" /> Cancel edit
            </button>
          )}
        </div>
        <div className="flex flex-wrap gap-1.5">
          <button onClick={() => aiAssist("polish")} disabled={!!aiBusy} className="text-xs px-2.5 py-1.5 rounded-md bg-[#D91E2A]/10 text-[#D91E2A] border border-[#D91E2A]/30 hover:bg-[#D91E2A]/20 font-semibold flex items-center gap-1 disabled:opacity-50" data-testid="ai-polish-btn">
            <Wand2 className="w-3 h-3" /> {aiBusy === "polish" ? "Polishing…" : "AI Polish"}
          </button>
          <button onClick={() => aiAssist("headline")} disabled={!!aiBusy} className="text-xs px-2.5 py-1.5 rounded-md bg-slate-100 text-slate-700 border border-slate-200 hover:bg-slate-200 font-semibold flex items-center gap-1 disabled:opacity-50" data-testid="ai-headline-btn">
            <Sparkles className="w-3 h-3" /> {aiBusy === "headline" ? "Thinking…" : "Suggest Headline"}
          </button>
          <button onClick={() => aiAssist("seo")} disabled={!!aiBusy} className="text-xs px-2.5 py-1.5 rounded-md bg-slate-100 text-slate-700 border border-slate-200 hover:bg-slate-200 font-semibold flex items-center gap-1 disabled:opacity-50" data-testid="ai-seo-btn">
            <Sparkles className="w-3 h-3" /> {aiBusy === "seo" ? "…" : "SEO Meta"}
          </button>
          <button onClick={perplexityBrief} disabled={!!aiBusy} className="text-xs px-2.5 py-1.5 rounded-md bg-slate-900 text-white border border-slate-900 hover:bg-slate-800 font-semibold flex items-center gap-1 disabled:opacity-50" data-testid="pplx-brief-btn" title="Prepend a fresh 'Latest developments' brief">
            <Bot className="w-3 h-3" /> {aiBusy === "brief" ? "Researching…" : "AI Brief"}
          </button>
        </div>
      </div>
      <div>
        <Label className="text-xs font-mono-fin uppercase text-slate-500">Title</Label>
        <Input className="mt-1.5 bg-slate-50 border-slate-200" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} data-testid="blog-title-input" />
      </div>
      <div>
        <Label className="text-xs font-mono-fin uppercase text-slate-500">Excerpt</Label>
        <Input className="mt-1.5 bg-slate-50 border-slate-200" value={form.excerpt} onChange={e => setForm({ ...form, excerpt: e.target.value })} data-testid="blog-excerpt-input" />
      </div>
      <div>
        <Label className="text-xs font-mono-fin uppercase text-slate-500">Content</Label>
        <div className="mt-1.5">
          <RichTextEditor value={form.content} onChange={html => setForm({ ...form, content: html })} placeholder="Write your article. Paste from Word / Docs / websites — formatting is preserved. Use the toolbar for headings, lists, and links." />
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <Label className="text-xs font-mono-fin uppercase text-slate-500">Category</Label>
          <Select value={form.category} onValueChange={v => setForm({ ...form, category: v })}>
            <SelectTrigger className="mt-1.5 bg-slate-50 border-slate-200" data-testid="blog-category-select"><SelectValue /></SelectTrigger>
            <SelectContent>{CATS.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs font-mono-fin uppercase text-slate-500">Author</Label>
          <Input className="mt-1.5 bg-slate-50 border-slate-200" value={form.author} onChange={e => setForm({ ...form, author: e.target.value })} />
        </div>
        <div className="sm:col-span-2">
          <CoverImagePicker value={form.cover_image} onChange={v => setForm({ ...form, cover_image: v })} />
        </div>
        <div className="sm:col-span-2">
          <Label className="text-xs font-mono-fin uppercase text-slate-500">Tags (comma-separated)</Label>
          <Input className="mt-1.5 bg-slate-50 border-slate-200" value={form.tags} onChange={e => setForm({ ...form, tags: e.target.value })} />
        </div>
      </div>
      <div className="flex items-center gap-2">
        <Switch checked={form.published} onCheckedChange={v => setForm({ ...form, published: v })} data-testid="blog-published-switch" />
        <Label className="text-sm">Publish immediately</Label>
      </div>
      <Button onClick={submit} disabled={busy} className="bg-[#D91E2A] hover:bg-[#B01820] text-white w-full sm:w-auto" data-testid="blog-submit-btn">
        {busy ? (isEdit ? "Updating…" : "Publishing…") : (isEdit ? "Update Post" : "Publish Post")}
      </Button>
    </div>
  );
}

function IPOForm({ onSaved }) {
  const empty = { company_name: "", status: "Upcoming", price_band: "", issue_size: "", open_date: "", close_date: "", listing_date: "", gmp: "", subscription: "", sector: "", lot_size: 0, description: "" };
  const [f, setF] = useState(empty);
  const [refreshing, setRefreshing] = useState(false);
  const submit = async () => {
    if (!f.company_name) return toast.error("Company name required");
    try {
      await api.post("/ipos", { ...f, lot_size: Number(f.lot_size) || null });
      toast.success("IPO added");
      setF(empty);
      onSaved?.();
    } catch { toast.error("Failed"); }
  };
  const refresh = async () => {
    setRefreshing(true);
    try {
      const r = await api.post("/ipos/refresh");
      toast.success(`Refreshed ${r.data.upserted} IPOs from IPO Watch`);
      onSaved?.();
    } catch { toast.error("Refresh failed"); }
    finally { setRefreshing(false); }
  };
  return (
    <div className="rounded-xl bg-white border border-slate-200 p-6 space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h3 className="font-heading font-bold text-lg">Add IPO</h3>
        <button onClick={refresh} disabled={refreshing} className="text-xs px-3 py-1.5 rounded-md bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100 font-semibold disabled:opacity-50" data-testid="ipo-refresh-btn">
          {refreshing ? "Refreshing…" : "Refresh from IPO Watch"}
        </button>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <Input placeholder="Company Name" className="bg-slate-50 border-slate-200 col-span-2" value={f.company_name} onChange={e => setF({ ...f, company_name: e.target.value })} data-testid="ipo-company-input" />
        <Select value={f.status} onValueChange={v => setF({ ...f, status: v })}>
          <SelectTrigger className="bg-slate-50 border-slate-200"><SelectValue /></SelectTrigger>
          <SelectContent>{["Upcoming", "Live", "Listed"].map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
        </Select>
        <Input placeholder="Sector" className="bg-slate-50 border-slate-200" value={f.sector} onChange={e => setF({ ...f, sector: e.target.value })} />
        <Input placeholder="Price Band (₹850 - ₹900)" className="bg-slate-50 border-slate-200" value={f.price_band} onChange={e => setF({ ...f, price_band: e.target.value })} />
        <Input placeholder="Issue Size (₹2,800 Cr)" className="bg-slate-50 border-slate-200" value={f.issue_size} onChange={e => setF({ ...f, issue_size: e.target.value })} />
        <Input placeholder="Open Date" className="bg-slate-50 border-slate-200" value={f.open_date} onChange={e => setF({ ...f, open_date: e.target.value })} />
        <Input placeholder="Close Date" className="bg-slate-50 border-slate-200" value={f.close_date} onChange={e => setF({ ...f, close_date: e.target.value })} />
        <Input placeholder="Listing Date" className="bg-slate-50 border-slate-200" value={f.listing_date} onChange={e => setF({ ...f, listing_date: e.target.value })} />
        <Input placeholder="GMP (+₹95)" className="bg-slate-50 border-slate-200" value={f.gmp} onChange={e => setF({ ...f, gmp: e.target.value })} />
        <Input placeholder="Subscription (3.2x)" className="bg-slate-50 border-slate-200" value={f.subscription} onChange={e => setF({ ...f, subscription: e.target.value })} />
        <Input placeholder="Lot Size" type="number" className="bg-slate-50 border-slate-200" value={f.lot_size} onChange={e => setF({ ...f, lot_size: e.target.value })} />
        <Textarea placeholder="Description" className="bg-slate-50 border-slate-200 col-span-2" value={f.description} onChange={e => setF({ ...f, description: e.target.value })} />
      </div>
      <Button onClick={submit} className="bg-[#D91E2A] hover:bg-[#B01820] text-white" data-testid="ipo-submit-btn"><Plus className="w-3.5 h-3.5 mr-1" /> Add IPO</Button>
    </div>
  );
}

function DRHPForm({ onSaved }) {
  const empty = { company_name: "", sector: "", filed_date: "", issue_size: "", sebi_link: "", pdf_url: "", summary: "", promoters: "", key_risks: "", objects_of_issue: "" };
  const [refreshing, setRefreshing] = useState(false);
  const [f, setF] = useState(empty);
  const submit = async () => {
    if (!f.company_name) return toast.error("Company name required");
    try {
      await api.post("/drhps", {
        ...f,
        key_risks: f.key_risks.split("\n").map(s => s.trim()).filter(Boolean),
        objects_of_issue: f.objects_of_issue.split("\n").map(s => s.trim()).filter(Boolean),
      });
      toast.success("DRHP added");
      setF(empty);
      onSaved?.();
    } catch { toast.error("Failed"); }
  };
  const refresh = async () => {
    setRefreshing(true);
    try {
      const r = await api.post("/drhps/refresh");
      toast.success(`Refreshed ${r.data.upserted} DRHP filings from IPO Watch`);
      onSaved?.();
    } catch { toast.error("Refresh failed"); }
    finally { setRefreshing(false); }
  };
  return (
    <div className="rounded-xl bg-white border border-slate-200 p-6 space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h3 className="font-heading font-bold text-lg">Add DRHP</h3>
        <button onClick={refresh} disabled={refreshing} className="text-xs px-3 py-1.5 rounded-md bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100 font-semibold disabled:opacity-50" data-testid="drhp-refresh-btn">
          {refreshing ? "Refreshing…" : "Refresh from IPO Watch"}
        </button>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <Input placeholder="Company Name" className="bg-slate-50 border-slate-200 col-span-2" value={f.company_name} onChange={e => setF({ ...f, company_name: e.target.value })} data-testid="drhp-company-input" />
        <Input placeholder="Sector" className="bg-slate-50 border-slate-200" value={f.sector} onChange={e => setF({ ...f, sector: e.target.value })} />
        <Input placeholder="Filed Date" className="bg-slate-50 border-slate-200" value={f.filed_date} onChange={e => setF({ ...f, filed_date: e.target.value })} />
        <Input placeholder="Issue Size" className="bg-slate-50 border-slate-200" value={f.issue_size} onChange={e => setF({ ...f, issue_size: e.target.value })} />
        <Input placeholder="Promoters" className="bg-slate-50 border-slate-200" value={f.promoters} onChange={e => setF({ ...f, promoters: e.target.value })} />
        <Input placeholder="SEBI Filing Link (URL)" className="bg-slate-50 border-slate-200 col-span-2 font-mono-fin" value={f.sebi_link} onChange={e => setF({ ...f, sebi_link: e.target.value })} />
        <Input placeholder="PDF URL (optional)" className="bg-slate-50 border-slate-200 col-span-2 font-mono-fin" value={f.pdf_url} onChange={e => setF({ ...f, pdf_url: e.target.value })} />
        <Textarea placeholder="Summary" className="bg-slate-50 border-slate-200 col-span-2" value={f.summary} onChange={e => setF({ ...f, summary: e.target.value })} />
        <Textarea placeholder="Key Risks (one per line)" className="bg-slate-50 border-slate-200" value={f.key_risks} onChange={e => setF({ ...f, key_risks: e.target.value })} />
        <Textarea placeholder="Objects of Issue (one per line)" className="bg-slate-50 border-slate-200" value={f.objects_of_issue} onChange={e => setF({ ...f, objects_of_issue: e.target.value })} />
      </div>
      <Button onClick={submit} className="bg-[#D91E2A] hover:bg-[#B01820] text-white" data-testid="drhp-submit-btn"><Plus className="w-3.5 h-3.5 mr-1" /> Add DRHP</Button>
    </div>
  );
}

function FilingForm({ onSaved }) {
  const empty = { company: "", symbol: "", exchange: "NSE", category: "Order Book Update", headline: "", order_value: "" };
  const [f, setF] = useState(empty);
  const submit = async () => {
    if (!f.company || !f.headline) return toast.error("Company & headline required");
    try {
      await api.post("/filings", f);
      toast.success("Filing added");
      setF(empty);
      onSaved?.();
    } catch { toast.error("Failed"); }
  };
  return (
    <div className="rounded-xl bg-white border border-slate-200 p-6 space-y-4">
      <h3 className="font-heading font-bold text-lg">Publish Filing / Breaking Alert</h3>
      <div className="grid grid-cols-2 gap-3">
        <Input placeholder="Company" className="bg-slate-50 border-slate-200" value={f.company} onChange={e => setF({ ...f, company: e.target.value })} data-testid="filing-company-input" />
        <Input placeholder="Symbol (e.g. RELIANCE)" className="bg-slate-50 border-slate-200 font-mono-fin" value={f.symbol} onChange={e => setF({ ...f, symbol: e.target.value })} />
        <Select value={f.exchange} onValueChange={v => setF({ ...f, exchange: v })}>
          <SelectTrigger className="bg-slate-50 border-slate-200"><SelectValue /></SelectTrigger>
          <SelectContent>{["NSE", "BSE", "MCX"].map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={f.category} onValueChange={v => setF({ ...f, category: v })}>
          <SelectTrigger className="bg-slate-50 border-slate-200"><SelectValue /></SelectTrigger>
          <SelectContent>{["Order Book Update", "Corporate Action", "SEBI Disclosure"].map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
        </Select>
        <Textarea placeholder="Headline" className="bg-slate-50 border-slate-200 col-span-2" value={f.headline} onChange={e => setF({ ...f, headline: e.target.value })} />
        <Input placeholder="Order Value (₹3,400 Cr) — optional" className="bg-slate-50 border-slate-200 col-span-2 font-mono-fin" value={f.order_value} onChange={e => setF({ ...f, order_value: e.target.value })} />
      </div>
      <Button onClick={submit} className="bg-[#D91E2A] hover:bg-[#B01820] text-white" data-testid="filing-submit-btn"><Plus className="w-3.5 h-3.5 mr-1" /> Publish</Button>
    </div>
  );
}

function ItemList({ items, onDelete, onEdit, render, testid }) {
  return (
    <div className="rounded-xl bg-white border border-slate-200 divide-y divide-slate-200 max-h-[500px] overflow-y-auto" data-testid={testid}>
      {items.map(it => (
        <div key={it.id} className="p-4 flex items-center justify-between gap-3 hover:bg-slate-50">
          <div className="flex-1 min-w-0">{render(it)}</div>
          <div className="flex items-center gap-2 shrink-0">
            {onEdit && (
              <button
                type="button"
                onClick={() => onEdit(it)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-[#D91E2A]/10 text-[#D91E2A] border border-[#D91E2A]/30 hover:bg-[#D91E2A]/20 text-xs font-semibold transition-colors"
                data-testid={`edit-${it.id}`}
                title="Edit this post"
              >
                <Pencil className="w-3.5 h-3.5" /> Edit
              </button>
            )}
            <button
              type="button"
              onClick={() => onDelete(it.id)}
              className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-slate-500 hover:bg-rose-50 hover:text-rose-600 border border-transparent hover:border-rose-200 transition-colors"
              data-testid={`delete-${it.id}`}
              title="Delete"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        </div>
      ))}
      {items.length === 0 && <div className="p-6 text-sm text-slate-500 font-mono-fin">Empty.</div>}
    </div>
  );
}

export default function AdminDashboard() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const isOwner = user?.role === "owner";
  const [editingBlog, setEditingBlog] = useState(null);
  const { data: stats } = useQuery({ queryKey: ["admin-stats"], queryFn: async () => (await api.get("/admin/stats")).data });
  const { data: blogs } = useQuery({ queryKey: ["admin-blogs"], queryFn: async () => (await api.get("/blogs?published_only=false")).data });
  const { data: ipos } = useQuery({ queryKey: ["admin-ipos"], queryFn: async () => (await api.get("/ipos")).data });
  const { data: drhps } = useQuery({ queryKey: ["admin-drhps"], queryFn: async () => (await api.get("/drhps")).data });
  const { data: filings } = useQuery({ queryKey: ["admin-filings"], queryFn: async () => (await api.get("/filings?limit=50")).data });

  const refresh = () => {
    qc.invalidateQueries();
  };

  const del = async (kind, id) => {
    if (!confirm("Delete?")) return;
    await api.delete(`/${kind}/${id}`);
    toast.success("Deleted");
    refresh();
  };

  return (
    <div className="px-4 sm:px-6 lg:px-12 py-8">
      <div className="flex items-center gap-3 mb-1">
        <BarChart3 className="w-5 h-5 text-[#D91E2A]" />
        <span className="text-xs font-mono-fin uppercase tracking-[0.3em] text-[#D91E2A]">Editorial Console</span>
      </div>
      <h1 className="font-heading font-extrabold text-4xl sm:text-5xl tracking-tight text-slate-900 mb-8">Admin Dashboard</h1>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
        <StatCard icon={Newspaper} label="Blogs Published" value={stats?.blogs_published ?? "—"} />
        <StatCard icon={FileText} label="Blogs Total" value={stats?.blogs_total ?? "—"} tone="text-cyan-400" />
        <StatCard icon={Rocket} label="IPOs Tracked" value={stats?.ipos ?? "—"} tone="text-orange-400" />
        <StatCard icon={FileText} label="DRHPs in Vault" value={stats?.drhps ?? "—"} tone="text-amber-400" />
        <StatCard icon={Newspaper} label="Filings" value={stats?.filings ?? "—"} tone="text-rose-400" />
      </div>

      <Tabs defaultValue="blogs">
        <TabsList className="bg-white border border-slate-200" data-testid="admin-tabs">
          <TabsTrigger value="blogs" data-testid="admin-tab-blogs">Blogs</TabsTrigger>
          <TabsTrigger value="ipos" data-testid="admin-tab-ipos">IPOs</TabsTrigger>
          <TabsTrigger value="drhps" data-testid="admin-tab-drhps">DRHP</TabsTrigger>
          <TabsTrigger value="filings" data-testid="admin-tab-filings">Filings / Alerts</TabsTrigger>
          <TabsTrigger value="research" data-testid="admin-tab-research">Research</TabsTrigger>
          {isOwner && <TabsTrigger value="editors" data-testid="admin-tab-editors">Editors</TabsTrigger>}
        </TabsList>

        <TabsContent value="blogs" className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
          <BlogEditor onSaved={refresh} editing={editingBlog} onCancelEdit={() => setEditingBlog(null)} />
          <ItemList
            items={blogs?.data || []}
            onDelete={id => del("blogs", id)}
            onEdit={setEditingBlog}
            testid="admin-blog-list"
            render={b => (
              <>
                <div className="font-medium text-slate-900 truncate">{b.title}</div>
                <div className="text-[10px] font-mono-fin uppercase text-slate-500 mt-1">{b.category} · {b.author} · {b.published ? "Published" : "Draft"}</div>
              </>
            )}
          />
        </TabsContent>

        <TabsContent value="ipos" className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
          <IPOForm onSaved={refresh} />
          <ItemList
            items={ipos?.data || []}
            onDelete={id => del("ipos", id)}
            testid="admin-ipo-list"
            render={i => (
              <>
                <div className="font-medium text-slate-900">{i.company_name}</div>
                <div className="text-[10px] font-mono-fin uppercase text-slate-500 mt-1">{i.status} · {i.price_band} · GMP {i.gmp}</div>
              </>
            )}
          />
        </TabsContent>

        <TabsContent value="drhps" className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
          <DRHPForm onSaved={refresh} />
          <ItemList
            items={drhps?.data || []}
            onDelete={id => del("drhps", id)}
            testid="admin-drhp-list"
            render={d => (
              <>
                <div className="font-medium text-slate-900">{d.company_name}</div>
                <div className="text-[10px] font-mono-fin uppercase text-slate-500 mt-1">{d.sector} · {d.issue_size} · {d.filed_date}</div>
              </>
            )}
          />
        </TabsContent>

        <TabsContent value="filings" className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
          <FilingForm onSaved={refresh} />
          <ItemList
            items={filings?.data || []}
            onDelete={id => del("filings", id)}
            testid="admin-filings-list"
            render={f => (
              <>
                <div className="text-sm text-slate-900 truncate">{f.headline}</div>
                <div className="text-[10px] font-mono-fin uppercase text-slate-500 mt-1">{f.company} · {f.exchange} · {f.category}</div>
              </>
            )}
          />
        </TabsContent>

        {isOwner && (
          <TabsContent value="editors" className="mt-6">
            <AdminEditors />
          </TabsContent>
        )}

        <TabsContent value="research" className="mt-6">
          <AskAIPanel testidPrefix="admin-ai" />
        </TabsContent>
      </Tabs>
    </div>
  );
}
